<template>
  <div class="att-panel">
    <div class="card-body p-0">
      <!-- Layer name and window controls -->
      <div class="att-header">
        <span class="att-title capitalize">{{ attributesName }}</span>

        <div class="att-header-actions">
          <button
            id="minOrMaxAttributeElement"
            type="button"
            class="att-icon-btn"
            :title="attributesMinimized ? 'Maximize Attributes' : 'Minimize Attributes'"
            @click="$emit(attributesMinimized ? 'maximizeAttributes' : 'minimizeAttributes')"
          >
            <fa :icon="['fas', attributesMinimized ? 'window-maximize' : 'window-minimize']" />
          </button>
          <button
            type="button"
            class="att-icon-btn att-close"
            title="Close"
            aria-label="Close"
            @click="closeAttTable()"
          >
            <fa :icon="['fas', 'times']" />
          </button>
        </div>
      </div>

      <!-- All attributes and their values -->
      <div v-show="!attributesMinimized" class="att-body scroll-Container">
        <div
          v-for="(resp, index) in attribResponse"
          :key="index"
          class="att-row"
        >
          <div class="att-name">{{ resp.name }}</div>
          <div
            :id="'editor' + index"
            class="att-value"
            title="Copy value"
            @click="copyText(resp.value)"
          >
            <span class="att-value-text">
              {{ resp.name == 'created_date' || resp.name == 'updated_date' ? resp.value ? resp.value.replace('Z', '') : resp.value : resp.value }}
            </span>
            <fa :icon="['fas', 'copy']" class="att-copy-icon" />
          </div>
        </div>
      </div>

      <!-- Pagination controls -->
      <div v-show="!attributesMinimized" class="att-footer">
        <div class="att-pager">
          <button
            type="button"
            class="att-pager-btn"
            :disabled="feature_index < 1"
            @click="getNextFeature(-1)"
          >
            <fa :icon="['fas', 'chevron-left']" />
          </button>
          <span class="att-pager-count">{{ feature_index + 1 }} of {{ features.length }}</span>
          <button
            type="button"
            class="att-pager-btn"
            :disabled="feature_index + 1 >= features.length"
            @click="getNextFeature(1)"
          >
            <fa :icon="['fas', 'chevron-right']" />
          </button>
        </div>
        <b-button class="close-btn" @click="closeAttTable()">Close</b-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ── Panel shell ────────────────────────────────────────── */
.att-panel {
  background: #ffffff;
  color: #063c6d;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(4, 30, 58, 0.28);
  overflow: hidden;
  font-family: 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── Header ──────────────────────────────────────────────── */
.att-header {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  background: linear-gradient(135deg, #0a5499, #063c6d);
  color: #fff;
}
.att-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.capitalize {
  text-transform: capitalize;
}
.att-header-actions {
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

/* Minimize / maximize / close */
.att-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.625rem;
  height: 1.625rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
}
.att-icon-btn:hover {
  background: rgba(255, 255, 255, 0.28);
  transform: scale(1.08);
}
.att-close:hover {
  background: #e5484d;
}

/* ── Attribute rows ──────────────────────────────────────── */
.att-body {
  max-height: 46vh;
  overflow-y: auto;
  overflow-x: hidden;
}
.att-body::-webkit-scrollbar {
  width: 8px;
}
.att-body::-webkit-scrollbar-thumb {
  background: rgba(6, 60, 109, 0.22);
  border-radius: 8px;
}
.att-body::-webkit-scrollbar-thumb:hover {
  background: rgba(6, 60, 109, 0.38);
}
.att-row {
  display: flex;
  align-items: stretch;
  padding: 0.4375rem 0.875rem;
  border-bottom: 1px solid rgba(6, 60, 109, 0.06);
}
.att-row:nth-child(even) {
  background-color: rgba(6, 60, 109, 0.035);
}
.att-name {
  flex: 0 0 42%;
  padding-right: 0.625rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(6, 60, 109, 0.6);
  word-break: break-word;
  align-self: center;
}
.att-value {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  min-width: 0;
  color: #063c6d;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.1875rem 0.5rem;
  border-radius: 6px;
  word-wrap: break-word;
  transition: background 0.12s ease;
}
.att-value-text {
  min-width: 0;
  word-break: break-word;
}
.att-copy-icon {
  flex: none;
  margin-left: auto;
  color: rgba(6, 60, 109, 0.35);
  font-size: 0.78rem;
  opacity: 0;
  transition: opacity 0.12s ease, color 0.12s ease;
}
.att-value:hover {
  background: rgba(10, 84, 153, 0.08);
}
.att-value:hover .att-copy-icon {
  opacity: 1;
  color: #0a5499;
}

/* ── Footer / pagination ─────────────────────────────────── */
.att-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.625rem;
  padding: 0.625rem 0.875rem;
  background: rgba(6, 60, 109, 0.04);
  border-top: 1px solid rgba(6, 60, 109, 0.08);
}
.att-pager {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.att-pager-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.875rem;
  height: 1.875rem;
  padding: 0;
  border: 1px solid rgba(6, 60, 109, 0.18);
  border-radius: 8px;
  background: #fff;
  color: #0a5499;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
}
.att-pager-btn:hover:not(:disabled) {
  background: #eef4fb;
  border-color: #0a5499;
  transform: translateY(-1px);
}
.att-pager-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.att-pager-count {
  font-size: 0.85rem;
  font-weight: 600;
  color: #063c6d;
  white-space: nowrap;
}
.close-btn {
  background: linear-gradient(135deg, #0a5499, #063c6d);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  padding: 0.375rem 1.375rem;
  transition: filter 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}
.close-btn:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(6, 60, 109, 0.3);
}
</style>

<script>
export default {
  name: 'MMP_Attributes',
  props: [
    'attributesName',
    'attributesMinimized',
    'attribResponse',
    'features',
    'feature_index'
  ],
  data() {
    return {
      disabledNext: false,
      disabledPrevious: false,
      index_item: null,
      names: null
    }
  },
  methods: {
    closeAttTable() {
      this.$emit('closeAttTable')
    },
    getNextFeature(modifier) {
      this.index_item = this.feature_index + modifier
      this.$emit('showNextAttribute', this.index_item)
    },
    copyText(attributeValue) {
      /* Writes value to user's clipboard and shows
      a toast that displays the copied data */
      if (String(attributeValue).includes('Z')) {
        // substring is contained in the string
        attributeValue=String(attributeValue).replace('Z','')
      }
     
      //Remove Z if exists from all the dates showing up in attribute panel.
      navigator.clipboard.writeText(attributeValue)
      this.$bvToast.toast(attributeValue, {
        autoHideDelay: 7000,
        title: 'Copied Value To Clipboard',
        toaster: 'b-toaster-top-right'
      })
    }
  }
}
</script>
